import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { generalActions } from "../../store/actions";
import resourcePlannerService from "./resourcePlanner.service";
import { getDefaultDateRange, getSummaryPeriodRange, getBufferedDateRange } from "./utils/dateUtils";
import moment from "moment";

const ResourcesPlannerContext = createContext(null);

export function useResourcesPlanner() {
  const ctx = useContext(ResourcesPlannerContext);
  if (!ctx) throw new Error("useResourcesPlanner must be used within ResourcesPlannerProvider");
  return ctx;
}

export function ResourcesPlannerProvider({ children }) {
  const dispatch = useDispatch();

  // View type
  const [viewType, setViewType] = useState("week"); // day | week | month

  // Date range
  const [dateRange, setDateRange] = useState(() => getDefaultDateRange("week"));

  // Summary period for resource allocation indicator
  const [summaryPeriod, setSummaryPeriod] = useState("this_week");

  // Custom date range for "custom" period
  const [customDateRange, setCustomDateRangeState] = useState(null);

  // Derived selected range (used for highlighting + calculations)
  const selectedRange = useMemo(() => {
    if (summaryPeriod === "custom" && customDateRange) {
      return customDateRange;
    }
    const range = getSummaryPeriodRange(summaryPeriod);
    return { startDate: range.startDate, endDate: range.endDate };
  }, [summaryPeriod, customDateRange]);

  // Allocation display mode: "hours" or "percentage"
  const [allocationDisplayMode, setAllocationDisplayMode] = useState("hours");

  // Filters
  const [projectFilter, setProjectFilter] = useState(null); // { id, name }
  const [resourceFilter, setResourceFilter] = useState([]); // [{ id, name }]

  // Data
  const [resources, setResources] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const pageSizeRef = useRef(10);
  const updatePageSize = useCallback((size) => { pageSizeRef.current = size; }, []);

  // Expanded state: { resourceId: { projectId: { scopeId: true } } }
  const [expanded, setExpanded] = useState({});

  // CreateEditTask modal
  const [taskModal, setTaskModal] = useState({
    isOpen: false,
    isEdit: false,
    taskId: null,
    resource: null,
    startDate: null,
    endDate: null,
    project: null,
    scope: null,
  });

  // Conflicts toasts
  const [conflictsToasts, setConflictsToasts] = useState([]);

  const addConflictsToast = useCallback((toast) => {
    setConflictsToasts((prev) => [...prev, toast]);
  }, []);

  const removeConflictsToast = useCallback((index) => {
    setConflictsToasts((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Floating cards
  const [floatingCards, setFloatingCards] = useState([]);

  const openFloatingCard = useCallback((task, scope, project, allocation, position) => {
    setFloatingCards((prev) => [
      ...prev,
      { id: `fc-${task.id}-${Date.now()}`, task, scope, project, allocation, position },
    ]);
  }, []);

  const closeFloatingCard = useCallback((cardId) => {
    setFloatingCards((prev) => prev.filter((c) => c.id !== cardId));
  }, []);

  // Track previous snapshot for rollback
  const rollbackRef = useRef(null);

  // ── Fetch data ──────────────────────────────────────────────────────────
  const fetchResources = useCallback(
    (page = 1, append = false) => {
      if (!dateRange.startDate || !dateRange.endDate) return;

      if (!append) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      const params = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        pageNumber: page,
        pageSize: pageSizeRef.current,
        projectId: projectFilter?.id || undefined,
        resources: resourceFilter.length > 0 ? resourceFilter.map((r) => r.id) : undefined,
      };

      resourcePlannerService
        .getPlanner(params)
        .then((response) => {
          const data = response.data || response || [];
          const pagination = response.headers?.["x-pagination"]
            ? JSON.parse(response.headers["x-pagination"])
            : null;

          if (append) {
            setResources((prev) => [...prev, ...data]);
          } else {
            setResources(data);
          }
          setHasMore(pagination?.HasNext ?? data.length === pageSizeRef.current);
          setPageNumber(page);
        })
        .catch(() => {
          dispatch(
            generalActions.showToast({
              title: "Failed to load planner data.",
              type: "error",
            })
          );
        })
        .finally(() => {
          setIsLoading(false);
          setIsLoadingMore(false);
        });
    },
    [dateRange, projectFilter, resourceFilter, dispatch]
  );

  // Fetch on filter/date change
  useEffect(() => {
    setPageNumber(1);
    fetchResources(1, false);
  }, [dateRange, projectFilter, resourceFilter]);

  // Load more
  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore) return;
    fetchResources(pageNumber + 1, true);
  }, [isLoadingMore, hasMore, pageNumber, fetchResources]);

  // ── Scroll to date (generalized) ────────────────────────────────────────
  const scrollToDateRef = useRef(null);

  const registerScrollToDate = useCallback((fn) => {
    scrollToDateRef.current = fn;
  }, []);

  const scrollToDate = useCallback((dateStr) => {
    setTimeout(() => {
      if (scrollToDateRef.current) {
        scrollToDateRef.current(dateStr);
      }
    }, 100);
  }, []);

  const scrollToToday = useCallback(() => {
    scrollToDate(moment().format("YYYY-MM-DD"));
  }, [scrollToDate]);

  // ── Scroll to top ────────────────────────────────────────────────────────
  const scrollToTopRef = useRef(null);

  const registerScrollToTop = useCallback((fn) => {
    scrollToTopRef.current = fn;
  }, []);

  // ── View type change ────────────────────────────────────────────────────
  const handleViewTypeChange = useCallback((newType) => {
    setViewType(newType);
    setDateRange(getDefaultDateRange(newType));
    // Scroll to top of list and center today after the new layout renders
    setTimeout(() => {
      if (scrollToTopRef.current) scrollToTopRef.current();
      if (scrollToDateRef.current) scrollToDateRef.current(moment().format("YYYY-MM-DD"));
    }, 150);
  }, [])

  // ── Period change (summary period dropdown) ─────────────────────────────
  const handlePeriodChange = useCallback((newPeriod) => {
    const wasCustom = summaryPeriod === "custom";
    setSummaryPeriod(newPeriod);
    if (newPeriod !== "custom" && wasCustom) {
      setCustomDateRangeState(null);
      setDateRange(getDefaultDateRange(viewType));
    }
    if (newPeriod !== "custom") {
      const range = getSummaryPeriodRange(newPeriod);
      scrollToDate(range.startDate);
    }
  }, [summaryPeriod, viewType, scrollToDate]);

  // ── Custom date range ───────────────────────────────────────────────────
  const setCustomDateRange = useCallback((startDate, endDate) => {
    setSummaryPeriod("custom");
    setCustomDateRangeState({ startDate, endDate });
    setDateRange(getBufferedDateRange(startDate, endDate));
    scrollToDate(startDate);
  }, [scrollToDate]);

  // ── Expand / collapse ──────────────────────────────────────────────────
  const toggleExpand = useCallback((resourceId, projectId, scopeId) => {
    setExpanded((prev) => {
      const next = { ...prev };
      if (scopeId) {
        // Toggle scope
        if (!next[resourceId]) next[resourceId] = {};
        if (!next[resourceId][projectId]) next[resourceId][projectId] = {};
        next[resourceId][projectId][scopeId] = !next[resourceId][projectId][scopeId];
      } else if (projectId) {
        // Toggle project
        if (!next[resourceId]) next[resourceId] = {};
        next[resourceId][projectId] = next[resourceId][projectId] ? false : {};
      } else {
        // Toggle resource
        next[resourceId] = next[resourceId] ? false : {};
      }
      return next;
    });
  }, []);

  const isExpanded = useCallback(
    (resourceId, projectId, scopeId) => {
      if (scopeId) return !!expanded[resourceId]?.[projectId]?.[scopeId];
      if (projectId) return !!expanded[resourceId]?.[projectId];
      return !!expanded[resourceId];
    },
    [expanded]
  );

  // ── Optimistic allocation update ────────────────────────────────────────
  const updateAllocation = useCallback(
    (taskId, resourceId, newStartDate, newEndDate) => {
      // Save rollback snapshot
      rollbackRef.current = JSON.parse(JSON.stringify(resources));

      // Optimistic update
      setResources((prev) =>
        prev.map((resource) => {
          if (resource.id !== resourceId) return resource;
          return {
            ...resource,
            projects: resource.projects.map((project) => ({
              ...project,
              scopes: project.scopes.map((scope) => ({
                ...scope,
                tasks: scope.tasks.map((task) => {
                  if (task.id !== taskId) return task;
                  return {
                    ...task,
                    allocation: {
                      ...task.allocation,
                      startDate: newStartDate,
                      endDate: newEndDate,
                    },
                  };
                }),
              })),
            })),
          };
        })
      );

      // API call
      resourcePlannerService
        .updateAllocation(taskId, resourceId, {
          startDate: newStartDate,
          endDate: newEndDate,
        })
        .then((response) => {
          // Update with server response
          const result = response?.value?.value;
          if (result && result.isPermissible === false) {
            const conflicts = result.conflicts || [];
            const hasCapacityConflict = conflicts.some(c => c.conflictType === "ResourceCapacity");
            addConflictsToast({
              type: hasCapacityConflict ? 0 : 1,
              title: hasCapacityConflict ? "Resource Capacity Conflict" : "Allocation Conflict",
              body: conflicts.map(c => c.message).join("<br/> <br/> "),
            });
            // Rollback
            if (rollbackRef.current) {
              setResources(rollbackRef.current);
              rollbackRef.current = null;
            }
            return;
          }

          if (result) {
            setResources((prev) =>
              prev.map((resource) => {
                if (resource.id !== resourceId) return resource;
                return {
                  ...resource,
                  projects: resource.projects.map((project) => ({
                    ...project,
                    scopes: project.scopes.map((scope) => ({
                      ...scope,
                      tasks: scope.tasks.map((task) => {
                        if (task.id !== taskId) return task;
                        return {
                          ...task,
                          allocation: result,
                        };
                      }),
                    })),
                  })),
                };
              })
            );
          }
          rollbackRef.current = null;
        })
        .catch(() => {
          // Rollback
          if (rollbackRef.current) {
            setResources(rollbackRef.current);
            rollbackRef.current = null;
          }
          dispatch(
            generalActions.showToast({
              title: "Failed to update allocation. Changes reverted.",
              type: "error",
            })
          );
        });
    },
    [resources, dispatch]
  );

  // ── Task modal ──────────────────────────────────────────────────────────
  const openCreateTask = useCallback((resource, startDate, endDate, project = null, scope = null) => {
    setTaskModal({
      isOpen: true,
      isEdit: false,
      taskId: null,
      resource,
      startDate,
      endDate,
      project,
      scope,
    });
  }, []);

  const openEditTask = useCallback((taskId) => {
    setTaskModal({
      isOpen: true,
      isEdit: true,
      taskId,
      resource: null,
      startDate: null,
      endDate: null,
      project: null,
      scope: null,
    });
  }, []);

  const closeTaskModal = useCallback(() => {
    setTaskModal({ isOpen: false, isEdit: false, taskId: null, resource: null, startDate: null, endDate: null, project: null, scope: null });
  }, []);

  const value = {
    // View
    viewType,
    setViewType: handleViewTypeChange,
    // Dates
    dateRange,
    setDateRange,
    // Summary period
    summaryPeriod,
    selectedRange,
    handlePeriodChange,
    setCustomDateRange,
    // Allocation display mode
    allocationDisplayMode,
    setAllocationDisplayMode,
    // Filters
    projectFilter,
    setProjectFilter,
    resourceFilter,
    setResourceFilter,
    // Data
    resources,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    updatePageSize,
    refetch: () => fetchResources(1, false),
    registerScrollToTop,
    // Expand
    expanded,
    toggleExpand,
    isExpanded,
    // Allocation
    updateAllocation,
    // Task modal
    taskModal,
    openCreateTask,
    openEditTask,
    closeTaskModal,
    // Conflicts toasts
    conflictsToasts,
    removeConflictsToast,
    // Floating cards
    floatingCards,
    openFloatingCard,
    closeFloatingCard,
    // Scroll
    registerScrollToDate,
    scrollToToday,
    scrollToDate,
  };

  return (
    <ResourcesPlannerContext.Provider value={value}>
      {children}
    </ResourcesPlannerContext.Provider>
  );
}
